import { useNavigate, useLocation } from "react-router-dom";

const EnrollmentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const enrollmentData = location.state?.enrollmentData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto text-center">
        <div className="mb-8">
          <div className="mx-auto w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-check-circle h-12 w-12 text-green-600"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <path d="m9 11 3 3L22 4"></path>
            </svg>
          </div>
        </div>
        
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Enrollment Submitted Successfully!</h1>
        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
          Thank you for choosing AQUA PRO SPORTS. We've received your enrollment request and our team will contact you within 24 hours to confirm your details and schedule.
        </p>
        
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 text-left">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Enrollment Details</h2>
          
          {/* Contact Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-600">Phone Number</p>
                <p className="font-medium">{enrollmentData?.contactInfo?.phone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email Address</p>
                <p className="font-medium">{enrollmentData?.contactInfo?.email}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600">Address</p>
              <p className="font-medium">{enrollmentData?.contactInfo?.address}</p>
            </div>
          </div>

          {/* Selected Plan */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Plan</h3>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-gray-900">{enrollmentData?.plan?.title}</h4>
                  <p className="text-sm text-gray-600">{enrollmentData?.plan?.description}</p>
                </div>
                <span className="text-xl font-bold text-gray-900">{enrollmentData?.plan?.price}</span>
              </div>
            </div>
          </div>
          
          {/* Participants Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {enrollmentData?.participantType === "child" ? "Children Information" : "Participants Information"}
            </h3>
            <div className="space-y-4">
              {enrollmentData?.participants?.map((participant, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">
                    {enrollmentData?.participantType === "child" ? `Child ${index + 1}` : `Participant ${index + 1}`}
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Full Name</p>
                      <p className="font-medium">{participant.fullName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Age</p>
                      <p className="font-medium">{participant.age}</p>
                    </div>
                  </div>
                  {participant.needsAssessment && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-600">Level Assessment Required</p>
                      <p className="font-medium">Yes</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Time Slots */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Preferred Time Slot</h3>
              <p className="text-gray-700">{enrollmentData?.timeSlot}</p>
            </div>
            {enrollmentData?.assessmentTimeSlot && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Assessment Time Slot</h3>
                <p className="text-gray-700">{enrollmentData?.assessmentTimeSlot}</p>
              </div>
            )}
          </div>
          
          {/* Next Steps */}
          <div className="pt-6 border-t">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">What happens next?</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* We'll Call You */}
              <div>
                <div className="flex items-start mb-4">
                  <div className="bg-blue-100 rounded-full p-2 mr-4 mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-phone h-5 w-5 text-blue-600"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">We'll Call You</h3>
                    <p className="text-gray-600 text-sm">Our enrollment specialist will contact you within 24 hours to confirm your details.</p>
                  </div>
                </div>
              </div>
              
              {/* Schedule Setup */}
              <div>
                <div className="flex items-start mb-4">
                  <div className="bg-green-100 rounded-full p-2 mr-4 mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-calendar h-5 w-5 text-green-600"
                    >
                      <path d="M8 2v4"></path>
                      <path d="M16 2v4"></path>
                      <rect width="18" height="18" x="3" y="4" rx="2"></rect>
                      <path d="M3 10h18"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Schedule Setup</h3>
                    <p className="text-gray-600 text-sm">We'll work with you to finalize the schedule that works best.</p>
                  </div>
                </div>
              </div>
              
              {/* Welcome Package */}
              <div>
                <div className="flex items-start mb-4">
                  <div className="bg-purple-100 rounded-full p-2 mr-4 mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-mail h-5 w-5 text-purple-600"
                    >
                      <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Welcome Package</h3>
                    <p className="text-gray-600 text-sm">You'll receive a welcome email with all necessary information.</p>
                  </div>
                </div>
              </div>
              
              {/* First Session */}
              <div>
                <div className="flex items-start mb-4">
                  <div className="bg-orange-100 rounded-full p-2 mr-4 mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-home h-5 w-5 text-orange-600"
                    >
                      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                      <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {enrollmentData?.assessmentTimeSlot ? "Assessment Session" : "First Session"}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {enrollmentData?.assessmentTimeSlot 
                        ? "Your swimming level assessment will be conducted first."
                        : "Your swimming journey begins with the first personalized session."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <button 
            onClick={() => { navigate('/enroll') }} 
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Enroll Another Participant
          </button>
          <button 
            onClick={() => { navigate('/') }} 
            className="w-full bg-white text-blue-600 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 border border-gray-300 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnrollmentSuccess;